import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { GetStartedForm } from '../components/GetStartedForm';
import { SalesForm } from '../components/SalesForm';
import { Check, HelpCircle, X, Plus, Minus, ChevronLeft, ChevronRight } from 'lucide-react';
export function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('yearly');
  // Form states
  const [isGetStartedFormOpen, setIsGetStartedFormOpen] = useState(false);
  const [isSalesFormOpen, setIsSalesFormOpen] = useState(false);
  // Feature comparison carousel state
  const [activePlan, setActivePlan] = useState(1); // 0: Starter, 1: Professional, 2: Enterprise
  const featureCarouselRef = useRef(null);
  const [featureTouchStart, setFeatureTouchStart] = useState(null);
  const [featureTouchEnd, setFeatureTouchEnd] = useState(null);
  // Pricing cards carousel state
  const [activeCard, setActiveCard] = useState(1); // 0: Starter, 1: Professional, 2: Enterprise
  const pricingCarouselRef = useRef(null);
  const [pricingTouchStart, setPricingTouchStart] = useState(null);
  const [pricingTouchEnd, setPricingTouchEnd] = useState(null);
  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;
  // Feature comparison carousel handlers
  const handleFeatureTouchStart = e => {
    setFeatureTouchEnd(null);
    setFeatureTouchStart(e.touches[0].clientX);
  };
  const handleFeatureTouchMove = e => {
    setFeatureTouchEnd(e.touches[0].clientX);
  };
  const handleFeatureTouchEnd = () => {
    if (!featureTouchStart || !featureTouchEnd) return;
    const distance = featureTouchStart - featureTouchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe && activePlan < 2) {
      setActivePlan(prev => prev + 1);
    }
    if (isRightSwipe && activePlan > 0) {
      setActivePlan(prev => prev - 1);
    }
  };
  // Pricing cards carousel handlers
  const handlePricingTouchStart = e => {
    setPricingTouchEnd(null);
    setPricingTouchStart(e.touches[0].clientX);
  };
  const handlePricingTouchMove = e => {
    setPricingTouchEnd(e.touches[0].clientX);
  };
  const handlePricingTouchEnd = () => {
    if (!pricingTouchStart || !pricingTouchEnd) return;
    const distance = pricingTouchStart - pricingTouchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe && activeCard < 2) {
      setActiveCard(prev => prev + 1);
    }
    if (isRightSwipe && activeCard > 0) {
      setActiveCard(prev => prev - 1);
    }
  };
  const goToPlan = index => {
    setActivePlan(index);
  };
  const goToCard = index => {
    setActiveCard(index);
  };
  // Form handlers
  const handleStartFreeTrial = () => {
    setIsGetStartedFormOpen(true);
  };
  const handleContactSales = () => {
    setIsSalesFormOpen(true);
  };
  return <div className="flex flex-col min-h-screen w-full" data-id="element-747">
      <Header data-id="element-748" />
      <main className="flex-grow" data-id="element-749">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24" data-id="element-750">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-751">
            <div className="text-center" data-id="element-752">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-753">
                Simple, Transparent Pricing
              </h1>
              <p className="text-xl md:text-2xl max-w-3xl mx-auto text-gray-300" data-id="element-754">
                Choose the plan that's right for your business. All plans
                include a 14-day free trial.
              </p>
            </div>
          </div>
        </div>
        {/* Pricing Toggle */}
        <div className="bg-white pt-16" data-id="element-755">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-756">
            <div className="flex justify-center mb-12" data-id="element-757">
              <div className="bg-gray-100 p-1 rounded-lg inline-flex" data-id="element-758">
                <button className={`px-4 py-2 rounded-md text-sm font-medium ${billingCycle === 'monthly' ? 'bg-black text-white' : 'text-gray-700'}`} onClick={() => setBillingCycle('monthly')} data-id="element-759">
                  Monthly
                </button>
                <button className={`px-4 py-2 rounded-md text-sm font-medium ${billingCycle === 'yearly' ? 'bg-black text-white' : 'text-gray-700'}`} onClick={() => setBillingCycle('yearly')} data-id="element-760">
                  Yearly <span className="text-xs opacity-75" data-id="element-761">Save 20%</span>
                </button>
              </div>
            </div>
            {/* Desktop Pricing Cards */}
            <div className="hidden md:grid md:grid-cols-3 gap-8 mb-16" data-id="element-762">
              <PricingCard name="Starter" description="Perfect for small businesses just getting started with AI automation." price={billingCycle === 'monthly' ? 299 : 239} billingCycle={billingCycle} features={['2 Active Agents', '1,000 Agent Tasks/month', 'Standard integrations', 'Email support', 'Basic analytics']} notIncluded={['Custom agent development', 'Advanced integrations', 'Priority support', 'Team collaboration']} buttonText="Start Free Trial" isPopular={false} onButtonClick={handleStartFreeTrial} data-id="element-763" />
              <PricingCard name="Professional" description="Ideal for growing businesses with multiple automation needs." price={billingCycle === 'monthly' ? 499 : 399} billingCycle={billingCycle} features={['5 Active Agents', '5,000 Agent Tasks/month', 'Advanced integrations', 'Priority email support', 'Advanced analytics', 'Custom agent development', 'Team collaboration']} notIncluded={['Dedicated account manager', 'Custom integrations']} buttonText="Start Free Trial" isPopular={true} onButtonClick={handleStartFreeTrial} data-id="element-764" />
              <PricingCard name="Enterprise" description="For organizations with complex automation requirements." price={billingCycle === 'monthly' ? 249 : 199} billingCycle={billingCycle} features={['Unlimited Active Agents', '25,000 Agent Tasks/month', 'All integrations', '24/7 priority support', 'Advanced analytics & reporting', 'Custom agent development', 'Team collaboration', 'Dedicated account manager', 'Custom integrations', 'On-premises deployment option']} buttonText="Contact Sales" isPopular={false} isEnterprise={true} onButtonClick={handleContactSales} data-id="element-765" />
            </div>
            {/* Mobile Pricing Cards Carousel */}
            <div className="md:hidden mb-16" ref={pricingCarouselRef} onTouchStart={handlePricingTouchStart} onTouchMove={handlePricingTouchMove} onTouchEnd={handlePricingTouchEnd} data-id="element-766">
              <div className="flex justify-between items-center mb-4" data-id="element-767">
                <button className={`p-2 rounded-full ${activeCard === 0 ? 'text-gray-300' : 'text-black'}`} onClick={() => activeCard > 0 && setActiveCard(activeCard - 1)} disabled={activeCard === 0} aria-label="Previous plan" data-id="element-768">
                  <ChevronLeft size={24} data-id="element-769" />
                </button>
                <div className="text-center" data-id="element-770">
                  <h3 className="text-xl font-bold" data-id="element-771">
                    {activeCard === 0 ? 'Starter' : activeCard === 1 ? 'Professional' : 'Enterprise'}
                  </h3>
                </div>
                <button className={`p-2 rounded-full ${activeCard === 2 ? 'text-gray-300' : 'text-black'}`} onClick={() => activeCard < 2 && setActiveCard(activeCard + 1)} disabled={activeCard === 2} aria-label="Next plan" data-id="element-772">
                  <ChevronRight size={24} data-id="element-773" />
                </button>
              </div>
              <div className="overflow-hidden" data-id="element-774">
                <div className="flex transition-transform duration-300 ease-in-out" style={{
                transform: `translateX(-${activeCard * 100}%)`
              }} data-id="element-775">
                  {/* Starter Card */}
                  <div className="w-full flex-shrink-0 px-1" data-id="element-776">
                    <PricingCard name="Starter" description="Perfect for small businesses just getting started with AI automation." price={billingCycle === 'monthly' ? 299 : 239} billingCycle={billingCycle} features={['2 Active Agents', '1,000 Agent Tasks/month', 'Standard integrations', 'Email support', 'Basic analytics']} notIncluded={['Custom agent development', 'Advanced integrations', 'Priority support', 'Team collaboration']} buttonText="Start Free Trial" isPopular={false} onButtonClick={handleStartFreeTrial} data-id="element-777" />
                  </div>
                  {/* Professional Card */}
                  <div className="w-full flex-shrink-0 px-1" data-id="element-778">
                    <PricingCard name="Professional" description="Ideal for growing businesses with multiple automation needs." price={billingCycle === 'monthly' ? 499 : 399} billingCycle={billingCycle} features={['5 Active Agents', '5,000 Agent Tasks/month', 'Advanced integrations', 'Priority email support', 'Advanced analytics', 'Custom agent development', 'Team collaboration']} notIncluded={['Dedicated account manager', 'Custom integrations']} buttonText="Start Free Trial" isPopular={true} onButtonClick={handleStartFreeTrial} data-id="element-779" />
                  </div>
                  {/* Enterprise Card */}
                  <div className="w-full flex-shrink-0 px-1" data-id="element-780">
                    <PricingCard name="Enterprise" description="For organizations with complex automation requirements." price={billingCycle === 'monthly' ? 249 : 199} billingCycle={billingCycle} features={['Unlimited Active Agents', '25,000 Agent Tasks/month', 'All integrations', '24/7 priority support', 'Advanced analytics & reporting', 'Custom agent development', 'Team collaboration', 'Dedicated account manager', 'Custom integrations', 'On-premises deployment option']} buttonText="Contact Sales" isPopular={false} isEnterprise={true} onButtonClick={handleContactSales} data-id="element-781" />
                  </div>
                </div>
              </div>
              {/* Carousel Indicators */}
              <div className="flex justify-center mt-4 space-x-2" data-id="element-782">
                {[0, 1, 2].map(index => <button key={index} onClick={() => goToCard(index)} className={`w-2.5 h-2.5 rounded-full ${activeCard === index ? 'bg-black' : 'bg-gray-300'}`} aria-label={`Go to ${index === 0 ? 'Starter' : index === 1 ? 'Professional' : 'Enterprise'} plan`} data-id="element-783" />)}
              </div>
              <div className="text-center mt-4 text-sm text-gray-500" data-id="element-784">
                <p data-id="element-785">Swipe to compare plans</p>
              </div>
            </div>
          </div>
        </div>
        {/* Feature Comparison */}
        <div className="bg-white py-16" data-id="element-786">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-787">
            <h2 className="text-3xl font-bold text-center mb-12" data-id="element-788">
              Compare Plans
            </h2>
            {/* Desktop View - Table */}
            <div className="hidden md:block overflow-x-auto" data-id="element-789">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-790">
                <thead data-id="element-791">
                  <tr data-id="element-792">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-793">
                      Feature
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-794">
                      Starter
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50" data-id="element-795">
                      Professional
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-796">
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-797">
                  <FeatureRow feature="Active Agents" starter="2" professional="5" enterprise="Unlimited" data-id="element-798" />
                  <FeatureRow feature="Agent Tasks/month" starter="1,000" professional="5,000" enterprise="25,000" data-id="element-799" />
                  <FeatureRow feature="Standard Integrations" starter={true} professional={true} enterprise={true} data-id="element-800" />
                  <FeatureRow feature="Advanced Integrations" starter={false} professional={true} enterprise={true} data-id="element-801" />
                  <FeatureRow feature="Custom Integrations" starter={false} professional={false} enterprise={true} data-id="element-802" />
                  <FeatureRow feature="Custom Agent Development" starter={false} professional={true} enterprise={true} data-id="element-803" />
                  <FeatureRow feature="Team Collaboration" starter={false} professional={true} enterprise={true} data-id="element-804" />
                  <FeatureRow feature="Analytics" starter="Basic" professional="Advanced" enterprise="Advanced" data-id="element-805" />
                  <FeatureRow feature="Support" starter="Email" professional="Priority Email" enterprise="24/7 Priority" data-id="element-806" />
                  <FeatureRow feature="Dedicated Account Manager" starter={false} professional={false} enterprise={true} data-id="element-807" />
                  <FeatureRow feature="On-premises Deployment" starter={false} professional={false} enterprise={true} data-id="element-808" />
                </tbody>
              </table>
            </div>
            {/* Mobile View - Carousel */}
            <div className="md:hidden" ref={featureCarouselRef} onTouchStart={handleFeatureTouchStart} onTouchMove={handleFeatureTouchMove} onTouchEnd={handleFeatureTouchEnd} data-id="element-809">
              <div className="flex justify-between items-center mb-4" data-id="element-810">
                <button className={`p-2 rounded-full ${activePlan === 0 ? 'text-gray-300' : 'text-black'}`} onClick={() => activePlan > 0 && setActivePlan(activePlan - 1)} disabled={activePlan === 0} data-id="element-811">
                  <ChevronLeft size={24} data-id="element-812" />
                </button>
                <div className="text-center" data-id="element-813">
                  <h3 className="text-xl font-bold" data-id="element-814">
                    {activePlan === 0 ? 'Starter' : activePlan === 1 ? 'Professional' : 'Enterprise'}
                  </h3>
                </div>
                <button className={`p-2 rounded-full ${activePlan === 2 ? 'text-gray-300' : 'text-black'}`} onClick={() => activePlan < 2 && setActivePlan(activePlan + 1)} disabled={activePlan === 2} data-id="element-815">
                  <ChevronRight size={24} data-id="element-816" />
                </button>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-817">
                <div className="overflow-hidden" data-id="element-818">
                  <div className="flex transition-transform duration-300 ease-in-out" style={{
                  transform: `translateX(-${activePlan * 100}%)`
                }} data-id="element-819">
                    {/* Starter Plan */}
                    <div className="w-full flex-shrink-0" data-id="element-820">
                      <MobilePlanFeatures planName="Starter" features={[{
                      name: 'Active Agents',
                      value: '2'
                    }, {
                      name: 'Agent Tasks/month',
                      value: '1,000'
                    }, {
                      name: 'Standard Integrations',
                      value: true
                    }, {
                      name: 'Advanced Integrations',
                      value: false
                    }, {
                      name: 'Custom Integrations',
                      value: false
                    }, {
                      name: 'Custom Agent Development',
                      value: false
                    }, {
                      name: 'Team Collaboration',
                      value: false
                    }, {
                      name: 'Analytics',
                      value: 'Basic'
                    }, {
                      name: 'Support',
                      value: 'Email'
                    }, {
                      name: 'Dedicated Account Manager',
                      value: false
                    }, {
                      name: 'On-premises Deployment',
                      value: false
                    }]} data-id="element-821" />
                    </div>
                    {/* Professional Plan */}
                    <div className="w-full flex-shrink-0" data-id="element-822">
                      <MobilePlanFeatures planName="Professional" isHighlighted={true} features={[{
                      name: 'Active Agents',
                      value: '5'
                    }, {
                      name: 'Agent Tasks/month',
                      value: '5,000'
                    }, {
                      name: 'Standard Integrations',
                      value: true
                    }, {
                      name: 'Advanced Integrations',
                      value: true
                    }, {
                      name: 'Custom Integrations',
                      value: false
                    }, {
                      name: 'Custom Agent Development',
                      value: true
                    }, {
                      name: 'Team Collaboration',
                      value: true
                    }, {
                      name: 'Analytics',
                      value: 'Advanced'
                    }, {
                      name: 'Support',
                      value: 'Priority Email'
                    }, {
                      name: 'Dedicated Account Manager',
                      value: false
                    }, {
                      name: 'On-premises Deployment',
                      value: false
                    }]} data-id="element-823" />
                    </div>
                    {/* Enterprise Plan */}
                    <div className="w-full flex-shrink-0" data-id="element-824">
                      <MobilePlanFeatures planName="Enterprise" features={[{
                      name: 'Active Agents',
                      value: 'Unlimited'
                    }, {
                      name: 'Agent Tasks/month',
                      value: '25,000'
                    }, {
                      name: 'Standard Integrations',
                      value: true
                    }, {
                      name: 'Advanced Integrations',
                      value: true
                    }, {
                      name: 'Custom Integrations',
                      value: true
                    }, {
                      name: 'Custom Agent Development',
                      value: true
                    }, {
                      name: 'Team Collaboration',
                      value: true
                    }, {
                      name: 'Analytics',
                      value: 'Advanced'
                    }, {
                      name: 'Support',
                      value: '24/7 Priority'
                    }, {
                      name: 'Dedicated Account Manager',
                      value: true
                    }, {
                      name: 'On-premises Deployment',
                      value: true
                    }]} data-id="element-825" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Carousel Indicators */}
              <div className="flex justify-center mt-4 space-x-2" data-id="element-826">
                {[0, 1, 2].map(index => <button key={index} onClick={() => goToPlan(index)} className={`w-2.5 h-2.5 rounded-full ${activePlan === index ? 'bg-black' : 'bg-gray-300'}`} aria-label={`Go to ${index === 0 ? 'Starter' : index === 1 ? 'Professional' : 'Enterprise'} plan`} data-id="element-827" />)}
              </div>
              <div className="text-center mt-4 text-sm text-gray-500" data-id="element-828">
                <p data-id="element-829">Swipe to compare plans</p>
              </div>
            </div>
          </div>
        </div>
        {/* FAQ Section */}
        <div className="bg-gray-50 py-16" data-id="element-830">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-831">
            <h2 className="text-3xl font-bold text-center mb-12" data-id="element-832">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto" data-id="element-833">
              <PricingFAQ question="What happens after my free trial ends?" answer="After your 14-day free trial, your account will automatically transition to the plan you selected. We'll send you reminders before the trial ends, and you can cancel or change your plan at any time." data-id="element-834" />
              <PricingFAQ question="Can I upgrade or downgrade my plan later?" answer="Yes, you can change your plan at any time. When upgrading, the new pricing and features take effect immediately. When downgrading, changes take effect at the end of your current billing cycle." data-id="element-835" />
              <PricingFAQ question="What counts as an 'Agent Task'?" answer="An Agent Task is counted each time your agent performs an action like responding to a customer inquiry, processing an order, or analyzing data. Simple actions like checking status don't count toward your task limit." data-id="element-836" />
              <PricingFAQ question="Do unused tasks roll over to the next month?" answer="No, unused tasks don't roll over. Your task allocation refreshes at the beginning of each billing cycle." data-id="element-837" />
              <PricingFAQ question="What payment methods do you accept?" answer="We accept all major credit cards (Visa, Mastercard, American Express, Discover) as well as PayPal. Enterprise customers can also pay by invoice." data-id="element-838" />
              <PricingFAQ question="Is there a setup fee?" answer="No, there are no setup fees for any of our plans. You only pay the advertised subscription price." data-id="element-839" />
            </div>
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-black text-white py-16" data-id="element-840">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-841">
            <h2 className="text-3xl font-bold mb-6" data-id="element-842">
              Ready to transform your business with AI?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-843">
              Start your 14-day free trial today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-844">
              <button onClick={handleStartFreeTrial} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-845">
                Start Free Trial
              </button>
              <button onClick={handleContactSales} className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors" data-id="element-846">
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-847" />
      {/* Forms */}
      <GetStartedForm isOpen={isGetStartedFormOpen} onClose={() => setIsGetStartedFormOpen(false)} data-id="element-848" />
      <SalesForm isOpen={isSalesFormOpen} onClose={() => setIsSalesFormOpen(false)} agentType="Pricing Inquiry" data-id="element-849" />
    </div>;
}
function MobilePlanFeatures({
  planName,
  features,
  isHighlighted = false
}) {
  const renderValue = value => {
    if (typeof value === 'boolean') {
      return value ? <Check className="text-black" size={20} data-id="element-850" /> : <X className="text-gray-400" size={20} data-id="element-851" />;
    }
    return <span className="font-medium" data-id="element-852">{value}</span>;
  };
  return <div className={`p-4 ${isHighlighted ? 'bg-gray-50' : ''}`} data-id="element-853">
      <div className="space-y-4" data-id="element-854">
        {features.map((feature, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-855">
            <span className="text-sm" data-id="element-856">{feature.name}</span>
            <div className="flex justify-center items-center" data-id="element-857">
              {renderValue(feature.value)}
            </div>
          </div>)}
      </div>
    </div>;
}
function PricingCard({
  name,
  description,
  price,
  billingCycle,
  features,
  notIncluded = [],
  buttonText,
  isPopular,
  isEnterprise = false,
  onButtonClick
}) {
  return <div className={`bg-white rounded-xl ${isPopular ? 'ring-2 ring-black shadow-lg' : 'border border-gray-200'} overflow-hidden`} data-id="element-858">
      {isPopular && <div className="bg-black text-white text-center py-2 text-sm font-medium" data-id="element-859">
          Most Popular
        </div>}
      <div className="p-6" data-id="element-860">
        <h3 className="text-2xl font-bold" data-id="element-861">{name}</h3>
        <p className="mt-2 text-gray-600 h-12" data-id="element-862">{description}</p>
        <div className="mt-4" data-id="element-863">
          {isEnterprise ? <div className="text-4xl font-bold" data-id="element-864">Custom</div> : <>
              <div className="flex items-end" data-id="element-865">
                <span className="text-4xl font-bold" data-id="element-866">${price}</span>
                <span className="text-gray-500 ml-2" data-id="element-867">/ mo</span>
              </div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-868">
                billed {billingCycle}
              </p>
            </>}
        </div>
        <div className="mt-6" data-id="element-869">
          <button onClick={onButtonClick} className={`block w-full text-center py-3 px-4 rounded-lg font-medium ${isPopular ? 'bg-black text-white hover:bg-gray-800' : 'border border-black text-black hover:bg-gray-50'} transition-colors`} data-id="element-870">
            {buttonText}
          </button>
        </div>
        <div className="mt-6" data-id="element-871">
          <p className="font-medium mb-2" data-id="element-872">Includes:</p>
          <ul className="space-y-2" data-id="element-873">
            {features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-874">
                <Check className="text-black mt-1 mr-2 flex-shrink-0" size={16} data-id="element-875" />
                <span data-id="element-876">{feature}</span>
              </li>)}
          </ul>
          {notIncluded.length > 0 && <>
              <p className="font-medium mb-2 mt-4 text-gray-500" data-id="element-877">
                Not included:
              </p>
              <ul className="space-y-2" data-id="element-878">
                {notIncluded.map((feature, index) => <li key={index} className="flex items-start text-gray-500" data-id="element-879">
                    <X className="text-gray-400 mt-1 mr-2 flex-shrink-0" size={16} data-id="element-880" />
                    <span data-id="element-881">{feature}</span>
                  </li>)}
              </ul>
            </>}
        </div>
      </div>
    </div>;
}
function FeatureRow({
  feature,
  starter,
  professional,
  enterprise
}) {
  const renderValue = value => {
    if (typeof value === 'boolean') {
      return value ? <Check className="mx-auto text-black" size={20} data-id="element-882" /> : <X className="mx-auto text-gray-400" size={20} data-id="element-883" />;
    }
    return value;
  };
  return <tr data-id="element-884">
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-885">
        {feature}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-886">
        {renderValue(starter)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center bg-gray-50 font-medium" data-id="element-887">
        {renderValue(professional)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-888">
        {renderValue(enterprise)}
      </td>
    </tr>;
}
function PricingFAQ({
  question,
  answer
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-889">
      <button onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full p-4 text-left bg-white hover:bg-gray-50 transition-colors" data-id="element-890">
        <div className="flex items-start" data-id="element-891">
          <HelpCircle className="text-black mr-3 mt-1 flex-shrink-0" size={20} data-id="element-892" />
          <h3 className="text-xl font-semibold" data-id="element-893">{question}</h3>
        </div>
        <div className="flex-shrink-0 ml-2" data-id="element-894">
          {isOpen ? <Minus className="text-black" size={20} data-id="element-895" /> : <Plus className="text-black" size={20} data-id="element-896" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-897">
        <p className="p-4 pt-0 text-gray-600 border-t border-gray-100" data-id="element-898">
          {answer}
        </p>
      </div>
    </div>;
}